/**
 * Worthly2Intro - w2_02. Shown once to existing users the first time they
 * open the update: try Worthly 2.0, or stay on their theme.
 *
 * Shows while userData.worthly2 is undefined (they've never chosen). Picking
 * either button saves worthly2, so it never shows again. New users never see
 * it: they start on Worthly 2.0 (worthly2: true).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { applyPlan } from '../../utils/plan'; // w2_03

// w2_03: Developer's "Preview Worthly 2.0 intro" sends this.
export const W2_INTRO_PREVIEW_EVENT = 'worthly:preview-w2-intro';

const C = {
  page: '#F3F0FA', ink: '#25222B', soft: '#8E8A99', line: '#EFECF5',
  lil: '#ECE6FA', lilT: '#7A5DD3', lilDot: '#8E74E3',
  mint: '#DFF2E7', blue: '#DFE9F9', butter: '#FCF3D2', butterT: '#8A7A3C',
};
const FONT = "'DM Sans', -apple-system, 'Helvetica Neue', sans-serif"; // w2_11

// w2_04: no paint emoji on the No problem card
export const shouldShowWorthly2Intro = (userData) =>
  !!userData && userData.onboarding_complete === true && userData.tutorial_completed !== false
  && userData.worthly2 === undefined && !userData.worthly2_only;

const Node = ({ e, label, done, on }) => (
  <div style={{ position: 'relative', zIndex: 1, flex: 1, textAlign: 'center' }}>
    <div style={{
      width: 40, height: 40, margin: '0 auto', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: done ? 14 : 17, color: done ? '#FFFFFF' : C.ink,
      background: done ? C.lilDot : '#FFFFFF', border: `3px solid ${done ? C.lilDot : on ? '#B9A6EE' : C.lil}`,
      boxShadow: on ? `0 0 0 4px ${C.lil}` : 'none',
    }}>{done ? '✓' : e}</div>
    <div style={{ fontSize: 11, marginTop: 6, color: on ? C.lilT : C.soft, fontWeight: on ? 600 : 400 }}>{label}</div>
  </div>
);

const Feat = ({ e, bg, title, text, first }) => (
  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '13px 0', borderTop: first ? 0 : `1px solid ${C.line}` }}>
    <span style={{ width: 44, height: 44, borderRadius: 15, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flex: 'none' }}>{e}</span>
    <span>
      <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink }}>{title}</span>
      <span style={{ display: 'block', fontSize: 13.5, color: C.soft, lineHeight: 1.45, marginTop: 2 }}>{text}</span>
    </span>
  </div>
);

export default function Worthly2Intro({ userData, saveUserData, onTried }) {
  const [step, setStep] = useState('intro'); // 'intro' | 'declined' | 'done'
  const [preview, setPreview] = useState(false); // w2_03
  useEffect(() => {
    const open = () => { setPreview(true); setStep('intro'); };
    window.addEventListener(W2_INTRO_PREVIEW_EVENT, open);
    return () => window.removeEventListener(W2_INTRO_PREVIEW_EVENT, open);
  }, []);
  if (step === 'done') return null;
  if (step === 'intro' && !preview && !shouldShowWorthly2Intro(userData)) return null;

  // w2_03: the new look goes on straight away, behind the intro, and the
  // intro only closes once it's there - so the old theme never flashes.
  const tryIt = () => {
    document.documentElement.setAttribute('data-theme', 'pink');
    applyPlan(true);
    saveUserData({ theme: 'pink', worthly2: true, worthly2_intro: 'tried' });
    if (onTried) onTried();
    setTimeout(() => { setPreview(false); setStep('done'); }, 250);
  };
  const noThanks = () => {
    saveUserData({ worthly2: false, worthly2_intro: 'declined' });
    setPreview(false);
    setStep('declined');
  };

  const btn = { display: 'block', width: '100%', border: 0, borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 600, fontFamily: FONT, background: '#1F1D24', color: '#FFFFFF' };

  if (step === 'declined') {
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, background: 'rgba(35,30,45,0.32)', fontFamily: FONT }} data-testid="worthly2-declined">
        <div style={{ width: '100%', maxWidth: 380, background: '#FFFFFF', borderRadius: 28, padding: '26px 22px 22px', textAlign: 'center' }}>
          <div style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', color: C.ink, marginTop: 0 }}>No problem! {'\u{1F60A}'}</div>
          <p style={{ fontSize: 14.5, color: C.soft, lineHeight: 1.45, margin: '6px 0 0' }}>You can try Worthly 2.0 any time from Settings, under Theme.</p>
          <div style={{ background: C.butter, borderRadius: 20, padding: '14px 16px', marginTop: 16, textAlign: 'left', fontSize: 14, color: '#5E5132', lineHeight: 1.45 }}>
            Your money timeline and every payday cards are only in Worthly 2.0!
          </div>
          <button type="button" onClick={() => setStep('done')} style={{ ...btn, marginTop: 16 }}>Got it</button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 700, background: C.page, overflowY: 'auto', fontFamily: FONT, WebkitOverflowScrolling: 'touch' }} data-testid="worthly2-intro">
      <div style={{ maxWidth: 460, margin: '0 auto', padding: 'calc(env(safe-area-inset-top, 0px) + 22px) 18px calc(env(safe-area-inset-bottom, 0px) + 18px)' }}>
        <div style={{ background: '#FFFFFF', borderRadius: 30, padding: '26px 22px 22px', textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>{'✨'}</div>
          <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.1, color: C.ink, marginTop: 10 }}>Worthly 2.0<br />is here!</div>
          <p style={{ fontSize: 14.5, lineHeight: 1.5, color: C.soft, margin: '10px 0 0' }}>
            Worthly has a new look, built around the moments you're saving for. Your budget, graphs and everything else are still here, mostly right where you left them!
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', marginTop: 18 }}>
            <div style={{ position: 'absolute', left: 24, right: 24, top: 19, height: 3, background: C.lil }} />
            <Node done label="Now" />
            <Node on e={'\u{1F6DF}'} label="Fund full" />
            <Node e={'✈️'} label="Trip" />
            <Node e={'\u{1F3E1}'} label="Home" />
            <Node e={'\u{1F33B}'} label="Retire" />
          </div>
        </div>
        <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '8px 18px', marginTop: 12 }}>
          <Feat first e={'\u{1F5FA}️'} bg={C.lil} title="Your money timeline" text="The moments you're working toward, right on your Home page. It's only in Worthly 2.0!" />
          <Feat e={'\u{1F4B8}'} bg={C.mint} title="Every payday" text="See where your money goes on its own each time you get paid." />
          <Feat e={'\u{1F331}'} bg={C.blue} title="Your wealth, step by step" text="Net worth milestones and funds you can see at a glance." />
        </div>
        <button type="button" onClick={tryIt} style={{ ...btn, marginTop: 16 }} data-testid="worthly2-try">Try it out</button>
        <button type="button" onClick={noThanks} style={{ display: 'block', width: '100%', border: 0, background: 'none', padding: 14, fontSize: 14.5, fontWeight: 600, color: C.soft, fontFamily: FONT }} data-testid="worthly2-no">No thanks</button>
      </div>
    </div>,
    document.body
  );
}

// w2_15
